import React, { useRef, useState, useEffect } from 'react';
import { useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Gauge, gaugeClasses } from '@mui/x-charts';
import { useTranslation } from 'react-i18next';
import Legend from '../Legend';
import ProgressBar from '../progressBar/ProgressBar';
import LocationStaffCard, { LocationStaffIndicator } from '../LocationStaffCard';
import { GaugeLabel, GaugeWrapper, ProgressBarWrapper } from './styledElements';
import { DashboardData } from '../../types/Dashboard';
import { DashboardCard } from '../Common/SurfaceCard';

interface CurrentHourEntriesProps {
    data: Pick<DashboardData, 'lastHour' | 'newCustomersTodayPercentage' | 'charts' | 'checkedinStaff'>;
};

const TYPOGRAPHY_HEIGHT = 36;
const MIN_GAUGE_HEIGHT = 50;
const MAX_GAUGE_WIDTH = 120;
const MAX_GAUGE_HEIGHT = 90;

export default function CurrentHourEntries({ data }: CurrentHourEntriesProps) {
    const theme = useTheme();
    const { t } = useTranslation("common");
    const gauge1Ref = useRef<HTMLDivElement>(null);
    const [gaugeSize, setGaugeSize] = useState({ width: 100, height: 80 });

    useEffect(() => {
        const observer = new ResizeObserver(([entry]) => {
            const { width, height } = entry.contentRect;
            setGaugeSize({
                width: Math.min(MAX_GAUGE_WIDTH, Math.floor(width)),
                height: Math.min(MAX_GAUGE_HEIGHT, Math.max(MIN_GAUGE_HEIGHT, Math.floor(height) - TYPOGRAPHY_HEIGHT)),
            });
        });

        if (gauge1Ref.current) observer.observe(gauge1Ref.current);

        return () => observer.disconnect();
    }, []);

    const titles = {
        day: [t("clients_today"), t("clients_yesterday"), t("clients_last_week")],
    };

    const progressBarsConfig = {
        day: [
            {
                current: data.charts.totalEntriesTodayYesterdayLastWeekThisTime.today,
                max: data.charts.totalEntriesTodayYesterdayLastWeekThisTime.max,
                color: theme.palette.primary.light,
            },
            {
                current: data.charts.totalEntriesTodayYesterdayLastWeekThisTime.yesterday,
                max: data.charts.totalEntriesTodayYesterdayLastWeekThisTime.max,
                color: theme.palette.primary.main,
            },
            {
                current: data.charts.totalEntriesTodayYesterdayLastWeekThisTime.lastWeek,
                max: data.charts.totalEntriesTodayYesterdayLastWeekThisTime.max,
                color: theme.palette.primary.dark,
            },
        ],
    };

    return (
        <DashboardCard
            title={t("current_time_entries_title")}
        >
            <Grid container columnSpacing={1.5} rowSpacing={1.5} sx={{ flex: 1, minHeight: 0, gridAutoRows: { md: '1fr' } }}>
                <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'column' }, gap: 1.5, position: 'relative' }}>
                    <GaugeWrapper ref={gauge1Ref}>
                        <Gauge
                            width={gaugeSize.width}
                            height={gaugeSize.height}
                            value={data.lastHour.current}
                            valueMax={data.lastHour.max}
                            valueMin={0}
                            title={t("clients_last_hour")}
                            startAngle={-90}
                            endAngle={90}
                            sx={(theme) => ({
                                [`& .${gaugeClasses.valueArc}`]: {
                                    fill: theme.palette.primary.light,
                                },
                                [`& .${gaugeClasses.valueText}`]: {
                                    fontSize: gaugeSize.height <= MIN_GAUGE_HEIGHT ? theme.typography.body1.fontSize : theme.typography.h5.fontSize,
                                    transform: 'translateY(-12%)',
                                },
                            })}
                        />
                        <GaugeLabel isSmall={gaugeSize.height <= MIN_GAUGE_HEIGHT}>{t("clients_last_hour")}</GaugeLabel>
                    </GaugeWrapper>

                    <GaugeWrapper>
                        <Gauge
                            width={gaugeSize.width}
                            height={gaugeSize.height}
                            value={data.newCustomersTodayPercentage.current}
                            valueMax={data.newCustomersTodayPercentage.max}
                            valueMin={0}
                            startAngle={-90}
                            endAngle={90}
                            title={t("new_clients_today")}
                            sx={(theme) => ({
                                [`& .${gaugeClasses.valueArc}`]: {
                                    fill: theme.palette.success.main,
                                },
                                [`& .${gaugeClasses.valueText}`]: {
                                    fontSize: gaugeSize.height <= MIN_GAUGE_HEIGHT ? theme.typography.body1.fontSize : theme.typography.h5.fontSize,
                                    transform: 'translateY(-12%)',
                                },
                            })}
                            text={({ value }) => `${value}%`}
                        />
                        <GaugeLabel isSmall={gaugeSize.height <= MIN_GAUGE_HEIGHT}>{t("new_clients_today")}</GaugeLabel>
                    </GaugeWrapper>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'grid', gap: 1.5, gridTemplateRows: { xs: 'auto auto', md: 'minmax(0, 1fr) auto' }, height: { md: '100%' }, overflow: { md: 'hidden' } }}>
                    <ProgressBarWrapper>
                        <ProgressBar
                            showLegend={false}
                            showPercentage={false}
                            progressBars={progressBarsConfig.day}
                            titleBars={titles.day}
                            sx={{ maxWidth: (theme) => theme.spacing(20), maxHeight: (theme) => theme.spacing(20) }}
                        />
                        <Legend
                            small
                            items={progressBarsConfig.day.map((bar, i) => ({
                                color: bar.color,
                                label: titles.day[i],
                            }))}
                        />
                    </ProgressBarWrapper>
                    <LocationStaffCard nameList={data.checkedinStaff} />
                </Grid>
            </Grid>
        </DashboardCard>
    );
};
